import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect, useCallback } from 'react';
import { GreenroomState } from '../stores/types/types';
import { ConnectedUser } from '../connection/socket';
import { userUpdated } from '../stores/actions/session';

/**
 * Hook to determine and manage timing leadership for media synchronization.
 *
 * The timing leader is responsible for sending sync timing updates to the beehive.
 * This hook implements the following logic:
 * 1. Find all eligible users (hosts/producers)
 * 2. Sort by email alphabetically
 * 3. The user with the most alphabetical email becomes the timing leader
 * 4. If a new user with a more alphabetical email arrives, leadership transfers
 */
export function useTimingLeader(): boolean {
	const dispatch = useDispatch();
	const connectedUsers = useSelector((state: GreenroomState) => state.SessionReducer.connectedUsers);
	const currentUser = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);

	// Find current user in connected users
	const currentConnectedUser = useMemo(() => {
		return connectedUsers.find((user: ConnectedUser) =>
			user.gr_user === currentUser?.gr_user
		);
	}, [connectedUsers, currentUser?.gr_user]);

	// Find all eligible users (hosts/producers) and determine who should be the timing leader
	const timingLeaderInfo = useMemo(() => {
		// Find all eligible users (current user if host, or any producer)
		const eligibleUsers = connectedUsers.filter((user: ConnectedUser) =>
			(host && user.gr_user === currentUser?.gr_user) || user.isProducer
		);

		if (eligibleUsers.length === 0) {
			return { shouldBeLeader: false, designatedLeader: null };
		}

		// Sort alphabetically by email
		const sortedEligibleUsers = eligibleUsers.sort((a, b) => {
			const aEmail = (a.email || '').toLowerCase();
			const bEmail = (b.email || '').toLowerCase();
			return aEmail.localeCompare(bEmail);
		});

		const designatedLeader = sortedEligibleUsers[0];
		const shouldBeLeader = designatedLeader?.gr_user === currentUser?.gr_user;

		return { shouldBeLeader, designatedLeader };
	}, [connectedUsers, host, currentUser?.gr_user]);

	// Update timing leader status
	const updateTimingLeaderStatus = useCallback((isLeader: boolean) => {
		if (currentConnectedUser && session) {
			const updatedUser: ConnectedUser = {
				...currentConnectedUser,
				timingLeader: isLeader,
			};

			dispatch(userUpdated(updatedUser));
		}
	}, [currentConnectedUser, session, dispatch]);

	// Effect to manage timing leader status
	useEffect(() => {
		if (!session || !currentConnectedUser) {
			return;
		}

		const { shouldBeLeader } = timingLeaderInfo;
		const currentlyLeader = currentConnectedUser.timingLeader === true;

		// Update status if it doesn't match what it should be
		if (shouldBeLeader !== currentlyLeader) {
			updateTimingLeaderStatus(shouldBeLeader);
		}
	}, [timingLeaderInfo, currentConnectedUser?.timingLeader, session, currentConnectedUser, updateTimingLeaderStatus]);

	// Return whether current user is the timing leader
	return timingLeaderInfo.shouldBeLeader;
}